import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  onSnapshot 
} from 'firebase/firestore';

/**
 * Create a new message (ticket)
 * @param {string} userId - User ID
 * @param {string} userEmail - User email
 * @param {string} subject - Message subject
 * @param {string} message - Message content
 * @param {string} type - Message type (help, question, refund, credit)
 * @returns {string} Message ID
 */
export async function createMessage(userId, userEmail, subject, message, type = 'question') {
  try {
    const now = new Date().toISOString(); // Use ISO string instead of serverTimestamp inside array
    const messageData = {
      userId,
      userEmail,
      subject,
      type,
      status: 'unread',
      messages: [
        {
          from: 'user',
          content: message,
          timestamp: now, // Use ISO string for timestamp inside array
        }
      ],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastReadByUser: null, // Track when user last read this conversation
      lastReadByAdmin: null, // Track when admin last read this conversation
    };

    const docRef = await addDoc(collection(db, 'messages'), messageData);
    console.log('✅ Message created:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating message:', error);
    throw error;
  }
}

/**
 * Reply to a message
 * @param {string} messageId - Message ID
 * @param {string} content - Reply content
 * @param {string} from - 'user' or 'admin'
 * @param {string} status - Optional status update
 */
export async function replyToMessage(messageId, content, from, status = null) {
  try {
    const messageRef = doc(db, 'messages', messageId);
    
    // Get current message to append to messages array
    const messageSnap = await getDocs(query(collection(db, 'messages'), where('__name__', '==', messageId)));
    
    if (messageSnap.empty) {
      throw new Error('Message not found');
    }

    const now = new Date().toISOString(); // Use ISO string instead of serverTimestamp inside array
    const currentData = messageSnap.docs[0].data();
    const updatedMessages = [
      ...(currentData.messages || []),
      {
        from,
        content,
        timestamp: now, // Use ISO string for timestamp inside array
      }
    ];

    const updateData = {
      messages: updatedMessages,
      updatedAt: serverTimestamp(),
    };

    // Update status if admin replies
    if (from === 'admin' && status) {
      updateData.status = status;
    } else if (from === 'admin' && currentData.status === 'unread') {
      updateData.status = 'pending';
    } else if (from === 'user' && currentData.status === 'resolved') {
      updateData.status = 'pending'; // Re-open if user replies after resolution
    }

    await updateDoc(messageRef, updateData);
    console.log('✅ Reply added to message:', messageId);
    return true;
  } catch (error) {
    console.error('❌ Error replying to message:', error);
    throw error;
  }
}

/**
 * Update message status
 * @param {string} messageId - Message ID
 * @param {string} status - New status (unread, pending, resolved)
 */
export async function updateMessageStatus(messageId, status) {
  try {
    const messageRef = doc(db, 'messages', messageId);
    await updateDoc(messageRef, {
      status,
      updatedAt: serverTimestamp(),
    });
    console.log('✅ Message status updated:', messageId, status);
    return true;
  } catch (error) {
    console.error('❌ Error updating message status:', error);
    throw error;
  }
}

/**
 * Get all messages for a user
 * @param {string} userId - User ID
 * @returns {array} Messages
 */
export async function getUserMessages(userId) {
  try {
    const q = query(
      collection(db, 'messages'),
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    
    return messages;
  } catch (error) {
    console.error('❌ Error fetching user messages:', error);
    return [];
  }
}

/**
 * Get all messages (admin)
 * @returns {array} All messages
 */
export async function getAllMessages() {
  try {
    const q = query(
      collection(db, 'messages'),
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const messages = [];
    
    querySnapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    
    return messages;
  } catch (error) {
    console.error('❌ Error fetching all messages:', error);
    return [];
  }
}

/**
 * Get unread message count for user
 * @param {string} userId - User ID
 * @returns {number} Unread count
 */
export async function getUnreadCountForUser(userId) {
  try {
    const q = query(
      collection(db, 'messages'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    let unreadCount = 0;
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Check if last message is from admin and status is not resolved
      if (data.messages && data.messages.length > 0) {
        const lastMessage = data.messages[data.messages.length - 1];
        if (lastMessage.from === 'admin' && data.status !== 'resolved') {
          unreadCount++;
        }
      }
    });
    
    return unreadCount;
  } catch (error) {
    console.error('❌ Error getting unread count:', error);
    return 0;
  }
}

/**
 * Get unread message count for admin
 * @returns {number} Unread count
 */
export async function getUnreadCountForAdmin() {
  try {
    const q = query(
      collection(db, 'messages'),
      where('status', 'in', ['unread', 'pending'])
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('❌ Error getting admin unread count:', error);
    return 0;
  }
}

/**
 * Subscribe to messages for real-time updates (user)
 * @param {string} userId - User ID
 * @param {function} callback - Callback function
 * @returns {function} Unsubscribe function
 */
export function subscribeToUserMessages(userId, callback) {
  const q = query(
    collection(db, 'messages'),
    where('userId', '==', userId),
    orderBy('updatedAt', 'desc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const messages = [];
    snapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    callback(messages);
  });
}

/**
 * Subscribe to all messages for real-time updates (admin)
 * @param {function} callback - Callback function
 * @returns {function} Unsubscribe function
 */
export function subscribeToAllMessages(callback) {
  const q = query(
    collection(db, 'messages'),
    orderBy('updatedAt', 'desc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const messages = [];
    snapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    callback(messages);
  });
}

/**
 * Mark message as read by user
 * @param {string} messageId - Message ID
 */
export async function markMessageAsReadByUser(messageId) {
  try {
    const messageRef = doc(db, 'messages', messageId);
    await updateDoc(messageRef, {
      lastReadByUser: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    console.log('✅ Message marked as read by user');
    return true;
  } catch (error) {
    console.error('❌ Error marking message as read:', error);
    return false;
  }
}

/**
 * Mark message as read by admin
 * @param {string} messageId - Message ID
 */
export async function markMessageAsReadByAdmin(messageId) {
  try {
    const messageRef = doc(db, 'messages', messageId);
    await updateDoc(messageRef, {
      lastReadByAdmin: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    console.log('✅ Message marked as read by admin');
    return true;
  } catch (error) {
    console.error('❌ Error marking message as read:', error);
    return false;
  }
}

